<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>

            <div class="page-header">
              <h2>IABF {{ adminFestivalYear }} — Store Items</h2>
              <div>
                <v-btn color="primary" class="mr-2" @click="openNew">+ New item</v-btn>
                <v-btn outlined class="mr-2" @click="bulkOpen = true">↑ Bulk import</v-btn>
                <v-btn outlined class="mr-2" href="/store?preview=1" target="_blank">👁 Preview store</v-btn>
                <v-btn class="mr-2" to="/iabf/admin/store/orders">↗ Orders</v-btn>
              </div>
            </div>

            <p class="muted">Toggle <strong>Published</strong> to make an item appear on the public storefront. The store-wide order deadline below disables checkout for everything after the date passes.</p>

            <div class="config-row mb-4">
              <label>Store open to shoppers:</label>
              <v-switch v-model="storeOpen" hide-details dense color="primary" @change="saveConfig" :label="storeOpen ? 'Live — anyone with the URL can shop' : 'Closed — visitors see a Coming soon page'" />
            </div>

            <div class="config-row mb-4">
              <label>Store order deadline (YYYY-MM-DD):</label>
              <input type="text" v-model="orderDeadline" placeholder="2026-06-25" class="deadline-input" @change="saveConfig" />
              <span class="muted small">After this date, dancers can browse but checkout is disabled.</span>
            </div>

            <div class="config-row mb-4">
              <label>Faculty unlock code:</label>
              <input type="text" v-model="facultyCode" placeholder="IABFFACULTY26" class="deadline-input" @change="saveConfig" />
              <span class="muted small">Share <code>{{ facultyStoreLink }}</code> with faculty. It auto-unlocks the free t-shirt benefit for them. Empty = no faculty link.</span>
            </div>

            <div v-if="loading" class="muted">Loading…</div>

            <table v-else class="items-table">
              <thead>
                <tr>
                  <th>Display order</th>
                  <th>Photos</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Price</th>
                  <th>Published</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in itemsSortedForAdmin" :key="item.id">
                  <td><input type="number" v-model.number="item.displayOrder" min="0" class="num-input" @change="saveItem(item)" /></td>
                  <td>
                    <div class="photo-pair">
                      <img v-if="item.frontPhotoUrl" :src="item.frontPhotoUrl" class="thumb" />
                      <img v-if="item.backPhotoUrl" :src="item.backPhotoUrl" class="thumb" />
                    </div>
                  </td>
                  <td>
                    <strong>{{ item.name || '(unnamed)' }}</strong>
                    <div class="sub">{{ item.description }}</div>
                  </td>
                  <td>{{ item.type }}</td>
                  <td>${{ ((item.priceCents || 0) / 100).toFixed(2) }}</td>
                  <td>
                    <v-icon v-if="item.published" color="green">mdi-check-circle</v-icon>
                    <v-icon v-else color="grey">mdi-circle-outline</v-icon>
                  </td>
                  <td>
                    <v-btn x-small outlined @click="openEdit(item)">Edit</v-btn>
                    <v-btn x-small text color="error" @click="onDelete(item)">Delete</v-btn>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Bulk import dialog -->
            <v-dialog v-model="bulkOpen" max-width="800" scrollable>
              <v-card>
                <v-card-title>Bulk import items</v-card-title>
                <v-card-text style="max-height: 70vh; overflow-y: auto;">
                  <p>
                    Paste tab- or comma-separated rows. Columns: <code>name</code>, <code>description</code>,
                    <code>type</code>, <code>price</code>, <code>displayOrder</code>. Header row optional.
                    Items are imported <strong>unpublished</strong> — publish them individually after editing.
                  </p>
                  <pre class="bulk-example">name	description	type	price	displayOrder
IABF Tee Black	Soft cotton	tshirt	25	10
IABF Sweatshirt	Cozy crewneck	cozy	45	20
IABF Magazine #5	Print magazine	magazine	20	30</pre>
                  <v-textarea v-model="bulkText" rows="10" auto-grow placeholder="Paste here…" />
                  <div v-if="bulkPreview.length" class="bulk-preview">
                    <strong>Will import {{ bulkPreview.length }} item{{ bulkPreview.length === 1 ? '' : 's' }}:</strong>
                    <table class="preview-table">
                      <thead><tr><th>Name</th><th>Type</th><th>Price</th><th>Order</th></tr></thead>
                      <tbody>
                        <tr v-for="(row, i) in bulkPreview" :key="i" :class="{ 'preview-error': row.error }">
                          <td>{{ row.name || '(missing)' }}</td>
                          <td>{{ row.type || '(missing)' }}</td>
                          <td>${{ (row.priceCents / 100).toFixed(2) }}</td>
                          <td>{{ row.displayOrder }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="bulkErrors.length" class="error--text mt-2">
                    <div v-for="(e, i) in bulkErrors" :key="i">{{ e }}</div>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="bulkOpen = false">Cancel</v-btn>
                  <v-spacer />
                  <v-btn color="primary" :loading="bulkSaving" :disabled="bulkPreview.length === 0 || bulkErrors.length > 0" @click="onBulkImport">Import {{ bulkPreview.length }} items</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Edit dialog -->
            <v-dialog v-model="editorOpen" max-width="720" scrollable>
              <v-card>
                <v-card-title>{{ form.id ? 'Edit item' : 'New item' }}</v-card-title>
                <v-card-text style="max-height: 75vh; overflow-y: auto;">
                  <v-text-field v-model="form.name" label="Item name *" />
                  <v-textarea v-model="form.description" label="Description" auto-grow rows="2" />
                  <v-select v-model="form.type" :items="typeOptions" label="Type *" />
                  <v-text-field v-model.number="form.priceDollars" label="Price (USD)" type="number" step="0.01" min="0" prefix="$" />
                  <v-text-field v-model.number="form.displayOrder" label="Display order (lower shows first)" type="number" min="0" />
                  <v-checkbox v-model="form.published" label="Published (visible to dancers)" />

                  <div class="form-section-label">Photos (optional)</div>
                  <div class="photo-section">
                    <div class="photo-slot">
                      <div class="photo-label">Front photo</div>
                      <div v-if="form.frontPhotoUrl" class="photo-wrap">
                        <img :src="form.frontPhotoUrl" class="big-thumb" />
                        <button type="button" class="photo-remove" title="Remove image" @click="removePhoto('frontPhotoUrl')">×</button>
                      </div>
                      <input type="file" accept="image/*" @change="onPhotoChosen($event, 'frontPhotoUrl')" />
                    </div>
                    <div class="photo-slot" v-if="form.type !== 'magazine'">
                      <div class="photo-label">Back photo</div>
                      <div v-if="form.backPhotoUrl" class="photo-wrap">
                        <img :src="form.backPhotoUrl" class="big-thumb" />
                        <button type="button" class="photo-remove" title="Remove image" @click="removePhoto('backPhotoUrl')">×</button>
                      </div>
                      <input type="file" accept="image/*" @change="onPhotoChosen($event, 'backPhotoUrl')" />
                    </div>
                  </div>

                  <div v-if="typeHasSizes(form.type)" class="form-section-label">Size chart PDF URL (optional — opens in a new tab)</div>
                  <v-text-field
                    v-if="typeHasSizes(form.type)"
                    v-model="form.sizeChartPdfUrl"
                    placeholder="https://… (e.g. printable ring sizer)"
                    hint="If set, the storefront shows 'Printable size guide ↗' instead of the inline chart."
                    persistent-hint
                  />

                  <div v-if="typeHasSizes(form.type)" class="form-section-label">Size chart (optional, plain text or pasted table)</div>
                  <v-textarea
                    v-if="typeHasSizes(form.type) && !form.sizeChartPdfUrl"
                    v-model="form.sizeChartText"
                    rows="4"
                    auto-grow
                    placeholder="Paste a sizing table or any text. Tabs / newlines preserved."
                  />
                  <div v-if="uploading" class="muted mt-2">Uploading photo…</div>
                  <div v-if="saveError" class="error--text mt-2">{{ saveError }}</div>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="editorOpen = false">Cancel</v-btn>
                  <v-spacer />
                  <v-btn color="primary" :disabled="!canSave" :loading="saving" @click="onSave">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { db, storage } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { ITEM_TYPES, typeHasSizes } from '@/utils/store'


function emptyForm () {
  return {
    id: null,
    name: '',
    description: '',
    type: 'tshirt',
    priceDollars: 0,
    displayOrder: 100,
    published: false,
    frontPhotoUrl: '',
    backPhotoUrl: '',
    sizeChartText: '',
    sizeChartPdfUrl: ''
  }
}

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      items: [],
      editorOpen: false,
      form: emptyForm(),
      saving: false,
      uploading: false,
      saveError: '',
      orderDeadline: '',
      facultyCode: '',
      storeOpen: false,
      typeOptions: ITEM_TYPES,
      bulkOpen: false,
      bulkText: '',
      bulkSaving: false
    }
  },
  computed: {
    canSave () {
      return !!this.form.name && this.form.type
    },
    itemsSortedForAdmin () {
      return this.items.slice().sort((a, b) => {
        const oa = Number(a.displayOrder) || 0
        const ob = Number(b.displayOrder) || 0
        if (oa !== ob) return oa - ob
        return (a.name || '').localeCompare(b.name || '')
      })
    },
    bulkParsed () {
      const lines = (this.bulkText || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean)
      if (lines.length === 0) return { rows: [], errors: [] }
      const errors = []
      const split = s => s.includes('\t') ? s.split('\t') : s.split(',').map(c => c.trim().replace(/^"|"$/g, ''))
      const HEADER_NAMES = ['name', 'description', 'type', 'price', 'displayorder']
      let startIdx = 0
      const firstCells = split(lines[0]).map(c => c.toLowerCase().trim())
      if (HEADER_NAMES.some(h => firstCells.includes(h))) startIdx = 1
      const rows = []
      for (let i = startIdx; i < lines.length; i++) {
        const cells = split(lines[i])
        const [name, description, type, priceRaw, displayOrderRaw] = cells
        const row = {
          name: (name || '').trim(),
          description: (description || '').trim(),
          type: (type || '').trim().toLowerCase(),
          priceCents: Math.round((parseFloat(priceRaw) || 0) * 100),
          displayOrder: Number(displayOrderRaw) || 100,
          error: false
        }
        if (!row.name) { errors.push(`Row ${i + 1}: missing name`); row.error = true }
        if (!ITEM_TYPES.includes(row.type)) { errors.push(`Row ${i + 1}: unknown type "${row.type}" (use one of: ${ITEM_TYPES.join(', ')})`); row.error = true }
        rows.push(row)
      }
      return { rows, errors }
    },
    bulkPreview () { return this.bulkParsed.rows },
    bulkErrors () { return this.bulkParsed.errors },
    facultyStoreLink () {
      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || 'https://broche-ballet-online.web.app'
      const code = (this.facultyCode || '').trim()
      if (!code) return '(no link — enter a code above)'
      return `${origin}/store?code=${encodeURIComponent(code)}`
    }
  },
  async created () {
    await this.load()
  },
  methods: {
    typeHasSizes,
    async load () {
      this.loading = true
      const [itemsSnap, configSnap] = await Promise.all([
        db.collection(this.IABF.storeItems).get(),
        db.collection(this.IABF.config).doc('store').get()
      ])
      this.items = itemsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      const cfg = configSnap.exists ? configSnap.data() : {}
      this.orderDeadline = cfg.orderDeadline || ''
      this.facultyCode = cfg.facultyCode || ''
      this.storeOpen = !!cfg.storeOpen
      this.loading = false
    },
    async saveConfig () {
      try {
        await db.collection(this.IABF.config).doc('store').set({
          orderDeadline: (this.orderDeadline || '').trim(),
          facultyCode: (this.facultyCode || '').trim(),
          storeOpen: !!this.storeOpen
        }, { merge: true })
      } catch (e) {
        console.error('Save store config failed:', e)
      }
    },
    async onBulkImport () {
      this.bulkSaving = true
      try {
        const batch = db.batch()
        for (const row of this.bulkPreview) {
          if (row.error) continue
          const ref = db.collection(this.IABF.storeItems).doc()
          batch.set(ref, {
            name: row.name,
            description: row.description,
            type: row.type,
            priceCents: row.priceCents,
            displayOrder: row.displayOrder,
            published: false,
            frontPhotoUrl: '',
            backPhotoUrl: '',
            sizeChartText: '',
            createdAt: new Date(),
            updatedAt: new Date()
          })
        }
        await batch.commit()
        this.bulkText = ''
        this.bulkOpen = false
        await this.load()
      } catch (e) {
        console.error('Bulk import failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Bulk import failed: ' + (e.message || ''))
      }
      this.bulkSaving = false
    },
    openNew () {
      this.form = emptyForm()
      this.saveError = ''
      this.editorOpen = true
    },
    openEdit (item) {
      this.form = {
        id: item.id,
        name: item.name || '',
        description: item.description || '',
        type: item.type || 'tshirt',
        priceDollars: ((item.priceCents || 0) / 100),
        displayOrder: Number(item.displayOrder) || 0,
        published: !!item.published,
        frontPhotoUrl: item.frontPhotoUrl || '',
        backPhotoUrl: item.backPhotoUrl || '',
        sizeChartText: item.sizeChartText || '',
        sizeChartPdfUrl: item.sizeChartPdfUrl || ''
      }
      this.saveError = ''
      this.editorOpen = true
    },
    removePhoto (field) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Remove this photo? You can upload a new one or leave it empty. Take effect when you Save.')) return
      this.$set(this.form, field, '')
    },
    async onPhotoChosen (event, field) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      this.uploading = true
      try {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, '_')
        const path = `iabf-store-2026/${Date.now()}-${safeName}`
        const ref = storage.ref(path)
        const snap = await ref.put(file, { contentType: file.type || 'image/jpeg' })
        const url = await snap.ref.getDownloadURL()
        this.$set(this.form, field, url)
      } catch (e) {
        console.error('Photo upload failed:', e)
        this.saveError = 'Photo upload failed: ' + (e.message || '')
      }
      this.uploading = false
    },
    async onSave () {
      this.saving = true
      this.saveError = ''
      try {
        const payload = {
          name: this.form.name.trim(),
          description: (this.form.description || '').trim(),
          type: this.form.type,
          priceCents: Math.round((this.form.priceDollars || 0) * 100),
          displayOrder: Number(this.form.displayOrder) || 0,
          published: !!this.form.published,
          frontPhotoUrl: this.form.frontPhotoUrl || '',
          backPhotoUrl: this.form.backPhotoUrl || '',
          sizeChartText: this.form.sizeChartText || '',
          sizeChartPdfUrl: this.form.sizeChartPdfUrl || '',
          updatedAt: new Date()
        }
        if (this.form.id) {
          await db.collection(this.IABF.storeItems).doc(this.form.id).update(payload)
        } else {
          payload.createdAt = new Date()
          await db.collection(this.IABF.storeItems).add(payload)
        }
        this.editorOpen = false
        await this.load()
      } catch (e) {
        console.error('Save failed:', e)
        this.saveError = 'Save failed: ' + (e.message || '')
      }
      this.saving = false
    },
    async saveItem (item) {
      // Quick-save for inline edits (display order).
      try {
        await db.collection(this.IABF.storeItems).doc(item.id).update({
          displayOrder: Number(item.displayOrder) || 0
        })
      } catch (e) {
        console.error('Inline save failed:', e)
      }
    },
    async onDelete (item) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete "${item.name}"? This can't be undone.`)) return
      try {
        await db.collection(this.IABF.storeItems).doc(item.id).delete()
        await this.load()
      } catch (e) {
        console.error('Delete failed:', e)
      }
    }
  }
}
</script>

<style scoped>
h2 { font-family: Quattrocento, serif; font-weight: 400; font-size: 28px; margin: 0; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.muted { color: #888; font-style: italic; }
.items-table { width: 100%; border-collapse: collapse; font-size: 14px; margin-top: 12px; }
.items-table th { text-align: left; padding: 8px; font-size: 12px; text-transform: uppercase; color: #999; border-bottom: 1px solid #eee; }
.items-table td { padding: 10px 8px; border-bottom: 1px solid #f3f1ed; vertical-align: top; }
.thumb { width: 50px; height: 50px; object-fit: cover; border-radius: 4px; margin-right: 4px; }
.big-thumb { display: block; max-width: 180px; max-height: 180px; margin: 4px 0; border-radius: 4px; }
.photo-wrap { position: relative; display: inline-block; }
.photo-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #dd4762;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.photo-remove:hover { background: #c43a54; }
.photo-pair { display: flex; gap: 4px; }
.photo-section { display: flex; gap: 18px; flex-wrap: wrap; }
.photo-slot { background: #fafaf8; padding: 10px; border-radius: 6px; min-width: 200px; }
.photo-label { font-size: 12px; font-weight: 600; color: #666; margin-bottom: 4px; }
.form-section-label { font-weight: 600; font-size: 13px; color: #555; margin: 12px 0 4px 0; text-transform: uppercase; letter-spacing: .04em; }
.sub { color: #888; font-size: 12px; margin-top: 2px; }
.num-input { width: 56px; padding: 4px; border: 1px solid #ddd; border-radius: 4px; }
.config-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; background: #fff8e1; padding: 10px 14px; border-radius: 6px; }
.config-row label { font-weight: 600; font-size: 14px; }
.deadline-input { padding: 6px 10px; border: 1px solid #ccc; border-radius: 4px; font-size: 14px; min-width: 140px; }
.small { font-size: 12px; }
.bulk-example { background: #fafaf8; padding: 10px 12px; border-radius: 4px; font-size: 12px; overflow-x: auto; white-space: pre; }
.bulk-preview { margin-top: 10px; }
.preview-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 6px; }
.preview-table th, .preview-table td { padding: 4px 8px; border-bottom: 1px solid #f3f1ed; text-align: left; }
.preview-table th { font-size: 11px; color: #999; text-transform: uppercase; }
.preview-error td { background: #ffebee; }
</style>
